import { createApi } from '@reduxjs/toolkit/query/react';

import { TagTypesEnum } from '@enum/tag-types.enum';
import { ResponseDataInterface } from '@interface/logs/response-data.interface';
import {
  AddMemberInputInterface,
  InviteInfoInterface,
  MemberChangeRolInputInterface,
  MemberInterface,
} from '@interface/member/member.interface';
import { portalQueryAuth } from '@util/api/portal-query-auth';
import { isExist } from '@util/data/is-exist';

export const membersApi = createApi({
  reducerPath: 'member-api',
  tagTypes: [TagTypesEnum.Members],
  baseQuery: portalQueryAuth('/members'),
  endpoints: (build) => ({
    loadMembers: build.query<ResponseDataInterface<MemberInterface[]>, string>({
      query: (account) => `/?account=${account}`,
      providesTags: [TagTypesEnum.Members],
    }),
    addMember: build.mutation<ResponseDataInterface<MemberInterface[]>, AddMemberInputInterface>({
      query: (body) => ({
        url: '/invite',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error) => (isExist(error) ? [] : [TagTypesEnum.Members]),
    }),
    acceptInvite: build.mutation<ResponseDataInterface<MemberInterface>, string>({
      query: (code) => ({
        url: `/invite/${code}/accept`,
        method: 'POST',
      }),
      invalidatesTags: (result, error) => (isExist(error) ? [] : [TagTypesEnum.Members]),
    }),
    getInviteInfo: build.query<ResponseDataInterface<InviteInfoInterface>, string>({
      query: (code) => ({
        url: `/invite/${code}/info`,
        method: 'POST',
      }),
    }),
    changeRole: build.mutation<ResponseDataInterface<MemberInterface[]>, MemberChangeRolInputInterface>({
      query: ({ id, account, role }) => ({
        url: `/role/${id}`,
        method: 'POST',
        body: { account, role },
      }),
      invalidatesTags: (result, error) => (isExist(error) ? [] : [TagTypesEnum.Members]),
    }),
    suspendMember: build.mutation<ResponseDataInterface<MemberInterface[]>, { id: string; account: string }>({
      query: ({ id, account }) => ({
        url: `/suspend/${id}`,
        method: 'POST',
        body: { account },
      }),
      invalidatesTags: (result, error) => (isExist(error) ? [] : [TagTypesEnum.Members]),
    }),
    deleteMember: build.mutation<ResponseDataInterface<MemberInterface[]>, { id: string; account: string }>({
      query: ({ id, account }) => ({
        url: `/${id}`,
        method: 'DELETE',
        body: { account },
      }),
      invalidatesTags: (result, error) => (isExist(error) ? [] : [TagTypesEnum.Members]),
    }),
  }),
});
